import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';

export default function WiegandSettingsTab({ deviceId, siteId, accountId, token }) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    mode: 0,
    useWiegandBypass: false,
    useFailCode: false,
    failCode: 0,
    outPulseWidth: 40,
    outPulseInterval: 10000,
    formats: [],
    slaveFormats: [],
    csnFormat: null,
    useWiegandUserID: 0,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (deviceId && token) {
      loadConfig();
    }
  }, [deviceId, token]);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cloudApi.getWiegandConfig(token, siteId, accountId, deviceId);
      if (response.data) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Error loading Wiegand config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to load Wiegand configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cloudApi.setWiegandConfig(token, siteId, accountId, {
        deviceId: deviceId,
        ...config,
      });
      enqueueSnackbar('Wiegand configuration saved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving Wiegand config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to save Wiegand configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <FormControl fullWidth>
          <InputLabel id="wiegand-mode-label">Wiegand Mode</InputLabel>
          <Select
            labelId="wiegand-mode-label"
            value={config.mode}
            label="Wiegand Mode"
            onChange={(e) => handleFieldChange('mode', e.target.value)}
          >
            <MenuItem value={0}>Wiegand IN Only</MenuItem>
            <MenuItem value={1}>Wiegand OUT Only</MenuItem>
            <MenuItem value={2}>Wiegand IN/OUT</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={config.useWiegandBypass}
              onChange={(e) => handleFieldChange('useWiegandBypass', e.target.checked)}
            />
          }
          label="Use Wiegand Bypass"
        />

        <FormControlLabel
          control={
            <Switch
              checked={config.useFailCode}
              onChange={(e) => handleFieldChange('useFailCode', e.target.checked)}
            />
          }
          label="Use Fail Code"
        />

        {config.useFailCode && (
          <TextField
            fullWidth
            type="number"
            label="Fail Code"
            value={config.failCode}
            onChange={(e) => handleFieldChange('failCode', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0, max: 255 }}
          />
        )}

        <TextField
          fullWidth
          type="number"
          label="Output Pulse Width (ms)"
          value={config.outPulseWidth}
          onChange={(e) => handleFieldChange('outPulseWidth', parseInt(e.target.value) || 40)}
          helperText="Range: 20-100 ms, Default: 40 ms"
          inputProps={{ min: 20, max: 100 }}
        />

        <TextField
          fullWidth
          type="number"
          label="Output Pulse Interval (ms)"
          value={config.outPulseInterval}
          onChange={(e) => handleFieldChange('outPulseInterval', parseInt(e.target.value) || 10000)}
          helperText="Range: 200-20000 ms, Default: 10000 ms"
          inputProps={{ min: 200, max: 20000 }}
        />

        <FormControl fullWidth>
          <InputLabel id="wiegand-user-id-label">User ID Output Type</InputLabel>
          <Select
            labelId="wiegand-user-id-label"
            value={config.useWiegandUserID}
            label="User ID Output Type"
            onChange={(e) => handleFieldChange('useWiegandUserID', e.target.value)}
          >
            <MenuItem value={0}>Unspecified</MenuItem>
            <MenuItem value={1}>Card ID</MenuItem>
            <MenuItem value={2}>User ID</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Advanced Format Configuration
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Wiegand formats define how card data is read and transmitted. Modifying these settings requires
            knowledge of your access control system's Wiegand format. The default configuration works with most
            standard 26-bit Wiegand systems.
          </Alert>

          {config.formats && config.formats.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<Icon icon="solar:alt-arrow-down-line-duotone" />}>
                <Typography>Formats ({config.formats.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {config.formats.map((format, index) => (
                    <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="body2">
                        Format ID: {format.formatID}, Length: {format.length} bits
                      </Typography>
                      {format.idFields && (
                        <Typography variant="caption" color="text.secondary">
                          ID Fields: {format.idFields.length}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {config.slaveFormats && config.slaveFormats.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<Icon icon="solar:alt-arrow-down-line-duotone" />}>
                <Typography>Slave Formats ({config.slaveFormats.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {config.slaveFormats.map((format, index) => (
                    <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="body2">
                        Format ID: {format.formatID}, Length: {format.length} bits
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {config.csnFormat && (
            <Accordion>
              <AccordionSummary expandIcon={<Icon icon="solar:alt-arrow-down-line-duotone" />}>
                <Typography>CSN Format</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="body2">
                    Format ID: {config.csnFormat.formatID}, Length: {config.csnFormat.length} bits
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={loadConfig} disabled={loading || saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Icon icon="solar:diskette-bold" />}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

